import { ISite } from '../site/interface';

export type BaselineMode = 'caiso' | 'static' | 'none';

export interface IBaselineMarketResult {
    /** which baseline/performance to render, or 'none' when sites span different markets */
    mode: BaselineMode;
    /** warning to display when baseline/performance can't be aggregated */
    warning?: string;
}

const PJM_MARKET = 'pjm';

/**
 * Decides which baseline/performance series to show based on a set of site markets:
 * - no pjm market            -> 'caiso' (default behaviour)
 * - every market is pjm      -> 'static'
 * - at least one pjm + a mix -> 'none' (can't aggregate across markets)
 *
 * A missing/empty market is treated as a distinct, non-pjm market.
 */
export function resolveBaselineMarketFromMarkets(markets: (string | null | undefined)[]): IBaselineMarketResult {
    const normalized = markets.map(market => market?.toLowerCase() || undefined);

    const hasPjm = normalized.some(market => market === PJM_MARKET);
    if (!hasPjm) {
        return { mode: 'caiso' };
    }

    const allPjm = normalized.every(market => market === PJM_MARKET);
    if (allPjm) {
        return { mode: 'static' };
    }

    const uniqMarkets = Array.from(new Set(normalized.map(market => (market ? market.toUpperCase() : 'Unknown'))));
    return {
        mode: 'none',
        warning: `Can't aggregate baseline/performance for sites in different energy markets: ${uniqMarkets.join(', ')}`,
    };
}

export function resolveBaselineMarket(sites: ISite[]): IBaselineMarketResult {
    return resolveBaselineMarketFromMarkets(sites.map(site => site.lmp_market));
}
